import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLine } from "./Reveal";
import { scrollToId } from "@/lib/site";
import { useLang } from "@/lib/i18n";

export default function Hero() {
  const ref = useRef(null);
  const { t, logo } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const logoY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const logoScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden px-5 pt-24 pb-16"
    >
      {/* Background layers */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#12131C] via-[#0B0C10] to-[#0B0C10]" />
      <div
        className="absolute left-1/2 top-1/3 -translate-x-1/2 h-[420px] w-[720px] rounded-full blur-[130px] opacity-25"
        style={{ background: "radial-gradient(circle, #0057B7 0%, transparent 70%)" }}
      />
      <div
        className="absolute right-[8%] bottom-[12%] h-[300px] w-[300px] rounded-full blur-[130px] opacity-20"
        style={{ background: "radial-gradient(circle, #E11B22 0%, transparent 70%)" }}
      />

      {/* EKG motif */}
      <svg
        className="absolute left-0 bottom-[18%] w-full h-24 opacity-40 pointer-events-none"
        viewBox="0 0 1000 100"
        preserveAspectRatio="none"
      >
        <motion.path
          d="M0,50 L340,50 L360,50 L375,20 L392,82 L410,10 L426,50 L470,50 L490,50 L505,35 L520,65 L534,50 L1000,50"
          fill="none"
          stroke="url(#ekgGrad)"
          strokeWidth="2"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 2.2, delay: 0.6, ease: "easeInOut" }}
        />
        <defs>
          <linearGradient id="ekgGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#0057B7" />
            <stop offset="100%" stopColor="#E11B22" />
          </linearGradient>
        </defs>
      </svg>

      <motion.div className="relative z-10 flex flex-col items-center text-center max-w-5xl">
        {/* Logo */}
        <motion.img
          src={logo}
          alt="Code Echo First Aid"
          style={{ y: logoY, scale: logoScale }}
          initial={{ opacity: 0, filter: "blur(12px)", y: 20 }}
          animate={{ opacity: 1, filter: "blur(0px)", y: 0 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-[560px] md:max-w-[640px] mb-10 drop-shadow-[0_20px_60px_rgba(0,87,183,0.25)]"
          data-testid="hero-logo"
        />

        <motion.div style={{ y: contentY }} className="flex flex-col items-center">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mb-6 text-[10px] sm:text-xs font-bold uppercase tracking-[0.35em] text-zinc-400"
          >
            {t.hero_badge}
          </motion.span>

          <h1 className="font-heading font-semibold uppercase tracking-tighter leading-[0.95] text-4xl sm:text-6xl md:text-7xl">
            <MaskLine delay={0.4}>{t.hero_l1}</MaskLine>
            <MaskLine delay={0.52}>
              <span className="text-white">{t.hero_strong}</span>
              {t.hero_l2_post}
            </MaskLine>
            <MaskLine delay={0.64}>
              <span className="bg-gradient-to-r from-[#4d9fff] via-white to-[#ff5a60] bg-clip-text text-transparent">
                {t.hero_l3}
              </span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-8 max-w-2xl text-sm sm:text-lg leading-relaxed text-zinc-200"
          >
            {t.hero_sub}
          </motion.p>

          <motion.button
            data-testid="hero-cta-button"
            onClick={() => scrollToId("form")}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            whileTap={{ scale: 0.97 }}
            className="group relative mt-11 overflow-hidden rounded-sm bg-[#D4AF37] px-8 py-4 text-xs sm:text-sm font-bold uppercase tracking-[0.18em] text-[#0B0C10] transition-all duration-300 hover:glow-gold"
          >
            <span className="relative z-10 flex items-center gap-3">
              {t.hero_cta}
            </span>
            <span className="absolute inset-0 -translate-y-full bg-[#e6c65a] transition-transform duration-300 group-hover:translate-y-0" />
          </motion.button>
        </motion.div>
      </motion.div>
    </section>
  );
}
